import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { ArrowRight, Settings } from 'lucide-react';

import { CreatorLinks } from './components/CreatorLinks';
import { ConsentGate } from './components/ConsentGate';
import { RehearsalStage } from './components/RehearsalStage';
import { CurveViz } from './components/CurveViz';
import { HistoryList } from './components/HistoryList';
import { CheckinsList } from './components/CheckinsList';

import {
  getWorkspace,
  getRehearsals,
  getCurvePoints,
  updateConsent,
  deleteAllData,
  exportAll,
  fetchServerRehearsals,
} from './lib/store';
import type { Rehearsal, WorkspaceConsent, CurvePoint } from './lib/types';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-[100svh] bg-[var(--bg)] text-[var(--text)]">
      <div className="max-w-[860px] mx-auto px-5">
        <header className="flex items-center justify-between py-6 border-b border-[var(--border)] mb-8">
          <Link to="/" className="font-medium tracking-tight text-xl">Courage Rehearsal</Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/curve" className="hover:text-[var(--accent)] transition">Your curve</Link>
            <Link to="/history" className="hover:text-[var(--accent)] transition">History</Link>
            <Link to="/checkins" className="hover:text-[var(--accent)] transition">Check-ins</Link>
            <Link to="/privacy" className="hover:text-[var(--accent)] transition">Privacy</Link>
            <Link to="/settings" className="text-[var(--text-muted)] hover:text-[var(--text)]"><Settings size={17} /></Link>
          </div>
        </header>
        {children}
        <CreatorLinks variant="footer" />
      </div>
    </div>
  );
}

function Landing() {
  const navigate = useNavigate();
  const ws = getWorkspace();
  const hasConsented = !!ws.consent.consent_granted_at;

  return (
    <div className="pt-12 pb-16">
      <div className="max-w-2xl">
        <div className="uppercase tracking-[4px] text-xs text-[var(--accent)] mb-3">FOR BURNED-OUT WORKERS</div>
        <h1 className="text-6xl sm:text-7xl font-medium tracking-[-2.8px] leading-[.92] mb-6">
          Rehearse being<br />wrong in private.
        </h1>
        <p className="text-2xl text-[var(--text-muted)] leading-tight max-w-[26ch]">
          So the fear has less power when you are in public.
        </p>
      </div>

      <div className="mt-12 max-w-prose text-[15px] leading-relaxed text-[var(--text-muted)]">
        This is not a fix, a hack, or a daily practice you are failing at. It is a quiet, finite rehearsal space.
        You begin with complicated gratitude for the part of you that dreads public error. You end with one better question, choice, or repair you can actually carry.
        The only output is your own <span className="text-[var(--text)]">ambiguity tolerance curve</span> — a private record of whether the charge moves.
      </div>

      <div className="mt-9 flex flex-wrap gap-3">
        <button
          onClick={() => navigate(hasConsented ? '/rehearse' : '/consent')}
          className="group inline-flex items-center gap-3 rounded-2xl bg-[var(--accent)] text-white px-7 py-3.5 text-[15px] font-medium hover:brightness-105 active:brightness-95 transition"
        >
          Begin a private rehearsal <ArrowRight className="group-hover:translate-x-0.5 transition" />
        </button>
        <button onClick={() => navigate('/curve')} className="inline-flex items-center gap-3 rounded-2xl border px-7 py-3.5 text-[15px] border-[var(--border)] hover:bg-[var(--bg-elev)]">
          See my curve
        </button>
      </div>

      <div className="mt-16">
        <CreatorLinks variant="hero" />
      </div>

      <div className="mt-12 text-xs text-[var(--text-subtle)] max-w-prose">
        No streaks. No points. No “you’re doing great”. No notifications that pull you back when you are trying to rest.
        Designed to respect depleted attention.
      </div>
    </div>
  );
}

function ConsentPage() {
  const navigate = useNavigate();
  return (
    <ConsentGate
      onProceed={() => navigate('/rehearse')}
      onLocalOnly={() => navigate('/rehearse')}
    />
  );
}

function RehearsePage() {
  const navigate = useNavigate();
  return (
    <RehearsalStage
      onComplete={(id) => {
        toast.success('Rehearsal saved. Your curve has been updated.', { duration: 2800 });
        navigate('/curve', { state: { justCompleted: id } });
      }}
      onExit={() => navigate('/')}
    />
  );
}

function CurvePage() {
  const [points, setPoints] = useState<CurvePoint[]>([]);

  function refresh() {
    setPoints(getCurvePoints());
  }

  useEffect(() => {
    refresh();
    // If cloud is allowed, attempt a silent pull so curve reflects any server-side check-ins
    const ws = getWorkspace();
    if (ws.consent.allow_server_storage) {
      fetchServerRehearsals(ws.id, ws.consent).then((serverRs) => {
        if (serverRs && serverRs.length > getRehearsals().length) {
          // In real impl we would merge intelligently; here we just toast that server has more
          toast.info('Some rehearsals exist on the server. Export + re-import or use the same workspace ID on this device to merge.', { duration: 5000 });
        }
      });
    }
  }, []);

  return (
    <Shell>
      <div className="pt-2 pb-16">
        <div className="mb-8">
          <div className="text-sm text-[var(--text-muted)]">Your private record</div>
          <h1 className="text-4xl font-medium tracking-tight">Ambiguity tolerance curve</h1>
        </div>
        <CurveViz points={points} />
        <div className="mt-8">
          <Link to="/rehearse" className="text-sm underline">Start another rehearsal →</Link>
        </div>
      </div>
    </Shell>
  );
}

function HistoryPage() {
  const [rehearsals, setRehearsals] = useState<Rehearsal[]>(() => getRehearsals());

  function refresh() {
    setRehearsals(getRehearsals());
  }

  return (
    <Shell>
      <div className="pt-2 pb-16">
        <h1 className="text-4xl font-medium tracking-tight mb-8">Private history</h1>
        <HistoryList rehearsals={rehearsals} onRefresh={refresh} />
      </div>
    </Shell>
  );
}

function CheckinsPage() {
  const [rehearsals, setRehearsals] = useState<Rehearsal[]>(() => getRehearsals());

  function refresh() {
    setRehearsals(getRehearsals());
  }

  return (
    <Shell>
      <div className="pt-2 pb-16">
        <div className="mb-8">
          <h1 className="text-4xl font-medium tracking-tight">Private check-ins</h1>
          <p className="text-[var(--text-muted)] mt-2 max-w-prose">These only exist if you enabled follow-up journeys when you saved a rehearsal. They appear here when you choose to open the app. No external reminders.</p>
        </div>
        <CheckinsList rehearsals={rehearsals} onRefresh={refresh} />
      </div>
    </Shell>
  );
}

function PrivacyPage() {
  return (
    <Shell>
      <div className="max-w-2xl py-6 text-[15px] leading-relaxed space-y-8 pb-20">
        <h1 className="text-4xl font-medium tracking-tight">How this instrument handles your attention and your data</h1>

        <div className="space-y-5 text-[var(--text-muted)]">
          <p>This is a product about attention that is deliberately built to avoid stealing it.</p>
          <p>No gamification. No variable rewards. No “come back tomorrow” streaks. No social proof. No infinite scroll of insights. The session is finite. You decide when it serves you.</p>
          <p>All raw rehearsal text stays under your workspace ID. If you never enable server storage, nothing ever leaves the device except what you explicitly export.</p>
        </div>

        <div>
          <div className="font-medium text-[var(--text)] mb-2">Captured only with explicit consent</div>
          <ul className="list-disc pl-5 space-y-1 text-[var(--text-muted)]">
            <li>Rumination theme (one of five common loops or your own words)</li>
            <li>Emotional residue (four dimensions) before and after</li>
            <li>Reappraisal success (how much the charge moved in the sitting)</li>
            <li>Repeat-loop frequency (your estimate of how often it visits)</li>
            <li>The gratitude text, scenario, reappraisal notes, and the final question/choice/repair</li>
          </ul>
        </div>

        <div>
          <div className="font-medium text-[var(--text)] mb-2">What we do with consented analytics</div>
          <p className="text-[var(--text-muted)]">Only coarse signals (rehearsal completed, which step took longest, whether a check-in was filled) go to Cloudflare Analytics Engine. No text. No themes that could re-identify you. Used to ask “is this instrument still respectful of depleted attention?” and then stop.</p>
        </div>

        <div>
          <div className="font-medium text-[var(--text)] mb-2">Your rights, always</div>
          <ul className="list-disc pl-5 space-y-1 text-[var(--text-muted)]">
            <li>Full JSON export of everything at any time</li>
            <li>One-click permanent deletion of the entire workspace (local + server if enabled)</li>
            <li>Change consent or retention period in Settings</li>
            <li>Use entirely locally forever</li>
          </ul>
        </div>

        <CreatorLinks variant="settings" />
      </div>
    </Shell>
  );
}

function SettingsPage() {
  const [ws, setWs] = useState(() => getWorkspace());
  const [exported, setExported] = useState('');

  function toggle(key: keyof WorkspaceConsent) {
    const next = updateConsent({ [key]: !ws.consent[key] } as any);
    setWs(next);
  }

  function doExport() {
    const data = exportAll();
    setExported(data);
    navigator.clipboard?.writeText(data).catch(() => {});
    toast.success('Full export copied to clipboard. Also saved below.');
  }

  function doDelete() {
    if (!confirm('This will permanently delete every rehearsal, check-in, and your workspace ID from this browser. Server data (if any) will remain until retention or manual deletion. Continue?')) return;
    deleteAllData();
    window.location.href = '/';
  }

  return (
    <Shell>
      <div className="max-w-xl py-4 pb-20">
        <h1 className="text-4xl font-medium tracking-tight mb-8">Settings &amp; sovereignty</h1>

        <div className="space-y-8 text-sm">
          <div>
            <div className="text-[var(--text-subtle)] text-xs mb-1">YOUR WORKSPACE ID</div>
            <div className="font-mono text-xs break-all bg-[var(--bg-elev)] border border-[var(--border)] p-3 rounded-2xl select-all">{ws.id}</div>
            <div className="text-xs text-[var(--text-muted)] mt-1.5">Copy this ID and paste it on another device to access the same curve and history (when cloud storage is enabled).</div>
          </div>

          <div className="space-y-3">
            <div className="font-medium">Consent toggles (change anytime)</div>
            {[
              ['allow_server_storage', 'Store rehearsals on server (D1) for cross-device curve'],
              ['allow_followups', 'Allow Workflows to create future private check-ins'],
              ['allow_analytics', 'Send coarse anonymous signals to Analytics Engine'],
            ].map(([key, label]) => (
              <label key={key} className="flex gap-3 items-start cursor-pointer">
                <input type="checkbox" checked={!!(ws.consent as any)[key]} onChange={() => toggle(key as any)} className="mt-1 accent-[var(--accent)]" />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <div className="pt-4 border-t border-[var(--border)] flex flex-wrap gap-3">
            <button onClick={doExport} className="rounded-2xl border px-5 py-2">Export everything (JSON)</button>
            <button onClick={doDelete} className="rounded-2xl border border-red-900/30 px-5 py-2 text-red-600/80 hover:text-red-600">Delete everything on this device</button>
          </div>

          {exported && (
            <pre className="text-[10px] bg-black/90 text-white p-4 rounded-2xl overflow-auto max-h-[320px]">{exported}</pre>
          )}
        </div>

        <CreatorLinks variant="settings" />
      </div>
    </Shell>
  );
}

export default function App() {
  // Global keyboard nicety: press ? to jump to curve
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === '?' && document.activeElement?.tagName !== 'TEXTAREA' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        window.location.href = '/curve';
      }
      if (e.key.toLowerCase() === 'r' && (e.metaKey || e.ctrlKey)) {
        // allow natural refresh
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Shell><Landing /></Shell>} />
        <Route path="/consent" element={<Shell><ConsentPage /></Shell>} />
        <Route path="/rehearse" element={<RehearsePage />} />
        <Route path="/curve" element={<CurvePage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/checkins" element={<CheckinsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes>
      <Toaster position="top-center" richColors closeButton />
    </BrowserRouter>
  );
}
